/**
 * Konfiguration für programmatische SEO-Landingpages.
 *
 * Quelle der Wahrheit für:
 *   - Bundesländer (Waschbox-Finder Regional-Seiten)
 *   - Orte des Einzugsgebiets (nur als Text, KEINE eigenen Seiten mehr)
 *   - Leistungsseiten (Aufbereiter, Keramikversiegelung, Lackpolitur)
 *   - Standortseiten (Niederlassung Saar-Pfalz in Blieskastel, Partnerbetrieb Eppelborn)
 *
 * Seit 11/2026 gibt es statt Leistung × Stadt (55 Seiten) nur noch 3 Leistungs-
 * und 2 Standortseiten. Die alten Stadtseiten leitet nginx per 301 um
 * (scripts/vps-configs/chrobe.de.conf, `$legacy_city_target`).
 */

// ─── BUNDESLÄNDER für /waschbox-finder/:region ──────────────────────────────
// 16 deutsche Bundesländer. Ein Slug = URL-Segment.
export const REGIONS = [
  { slug: 'saarland', name: 'Saarland', short: 'SL', center: [49.4, 7.0], radius: 60, population: '1,0 Mio' },
  { slug: 'rheinland-pfalz', name: 'Rheinland-Pfalz', short: 'RP', center: [49.95, 7.5], radius: 130, population: '4,1 Mio' },
  { slug: 'baden-wuerttemberg', name: 'Baden-Württemberg', short: 'BW', center: [48.6, 9.0], radius: 160, population: '11,3 Mio' },
  { slug: 'bayern', name: 'Bayern', short: 'BY', center: [48.95, 11.5], radius: 220, population: '13,2 Mio' },
  { slug: 'hessen', name: 'Hessen', short: 'HE', center: [50.6, 9.0], radius: 130, population: '6,3 Mio' },
  { slug: 'nordrhein-westfalen', name: 'Nordrhein-Westfalen', short: 'NRW', center: [51.4, 7.5], radius: 170, population: '17,9 Mio' },
  { slug: 'niedersachsen', name: 'Niedersachsen', short: 'NI', center: [52.6, 9.5], radius: 200, population: '8,0 Mio' },
  { slug: 'sachsen', name: 'Sachsen', short: 'SN', center: [51.1, 13.5], radius: 130, population: '4,1 Mio' },
  { slug: 'sachsen-anhalt', name: 'Sachsen-Anhalt', short: 'ST', center: [51.95, 11.7], radius: 120, population: '2,2 Mio' },
  { slug: 'thueringen', name: 'Thüringen', short: 'TH', center: [50.9, 11.0], radius: 110, population: '2,1 Mio' },
  { slug: 'brandenburg', name: 'Brandenburg', short: 'BB', center: [52.4, 13.4], radius: 150, population: '2,5 Mio' },
  { slug: 'mecklenburg-vorpommern', name: 'Mecklenburg-Vorpommern', short: 'MV', center: [53.7, 12.5], radius: 160, population: '1,6 Mio' },
  { slug: 'schleswig-holstein', name: 'Schleswig-Holstein', short: 'SH', center: [54.2, 9.7], radius: 120, population: '2,9 Mio' },
  { slug: 'hamburg', name: 'Hamburg', short: 'HH', center: [53.55, 10.0], radius: 30, population: '1,9 Mio' },
  { slug: 'bremen', name: 'Bremen', short: 'HB', center: [53.07, 8.8], radius: 25, population: '0,7 Mio' },
  { slug: 'berlin', name: 'Berlin', short: 'BE', center: [52.52, 13.4], radius: 35, population: '3,7 Mio' },
];

export const REGION_BY_SLUG = Object.fromEntries(REGIONS.map((r) => [r.slug, r]));

// ─── ORTE DES EINZUGSGEBIETS (nur Text, keine eigenen Seiten) ─────────────
// Werden auf /einzugsgebiet, in Standortseiten und im Schema (areaServed)
// genannt. Eine eigene URL pro Ort ergab 55 fast gleiche Seiten.
export const CITIES = [
  { slug: 'eppelborn',    name: 'Eppelborn',    distance: 0,    context: 'Hauptsitz',           lat: 49.4027, lon: 6.9647 },
  { slug: 'saarbruecken', name: 'Saarbrücken',  distance: 25,   context: 'Landeshauptstadt',    lat: 49.2354, lon: 6.9969 },
  { slug: 'saarlouis',    name: 'Saarlouis',    distance: 22,   context: 'Kreisstadt',          lat: 49.3134, lon: 6.7519 },
  { slug: 'neunkirchen',  name: 'Neunkirchen',  distance: 12,   context: 'Kreisstadt',          lat: 49.3474, lon: 7.1794 },
  { slug: 'homburg',      name: 'Homburg',      distance: 35,   context: 'Universitätsstadt',   lat: 49.3253, lon: 7.3369 },
  { slug: 'sankt-wendel', name: 'St. Wendel',   distance: 25,   context: 'Kreisstadt',          lat: 49.4664, lon: 7.1687 },
  { slug: 'merzig',       name: 'Merzig',       distance: 40,   context: 'Kreisstadt',          lat: 49.4437, lon: 6.6389 },
  { slug: 'voelklingen',  name: 'Völklingen',   distance: 30,   context: 'Industriestadt',     lat: 49.2513, lon: 6.8583 },
  { slug: 'lebach',       name: 'Lebach',       distance: 12,   context: 'Mittelstadt',         lat: 49.4103, lon: 6.9089 },
  { slug: 'illingen',     name: 'Illingen',     distance: 8,    context: 'Nachbargemeinde',    lat: 49.3773, lon: 7.0489 },
  { slug: 'saarland',     name: 'Saarland',     distance: null, context: 'Bundesland',          lat: 49.4,    lon: 7.0    },
];

export const CITY_BY_SLUG = Object.fromEntries(CITIES.map((c) => [c.slug, c]));

// ─── LEISTUNGSSEITEN ────────────────────────────────────────────────────────
// Fahrzeugpflege und Detailing sind keine eigenen Seiten mehr: Google sah sie
// als Dubletten von „Aufbereiter". Sie stehen als Abschnitte auf /aufbereiter.
export const SERVICES = [
  {
    slug: 'aufbereiter',
    name: 'Aufbereiter',
    plural: 'Aufbereitung',
    seoTitle: 'Aufbereiter Saarland — Fahrzeugaufbereitung & Detailing',
    seoDescription: 'Aufbereiter im Saarland: Innen- und Außenaufbereitung, Fahrzeugpflege und Detailing. Mobil, in Blieskastel oder Eppelborn. Festpreis, Termin online.',
    bulletsTitle: 'Das bekommst du bei der Aufbereitung',
    h1: 'Aufbereiter im Saarland — Fahrzeugaufbereitung, Pflege & Detailing',
    headline: 'Fahrzeugaufbereitung, Fahrzeugpflege & Detailing',
    intro: 'Professionelle Fahrzeugaufbereitung auf Werkstatt-Niveau: Innenreinigung, Handwäsche, Lederpflege, Detailing und Pflege-Abo aus einer Hand.',
    bullets: [
      'Vollumfängliche Innen- und Außenaufbereitung mit Profi-Produkten',
      'Handwäsche statt Bürstenanlage — null Risiko für den Lack',
      'Lederpflege, Cabrioverdeck-Imprägnierung, Felgenversiegelung, Motorraum auf Wunsch',
      'Persönlicher Servicechef, individuelles Angebot, Festpreis-Garantie',
      'Mobil bei dir, an der Niederlassung in Blieskastel oder beim Partnerbetrieb in Eppelborn',
    ],
  },
  {
    slug: 'keramikversiegelung',
    name: 'Keramikversiegelung',
    plural: 'Keramikversiegelungen',
    seoTitle: 'Keramikversiegelung Saarland — Lackschutz bis 5 Jahre',
    seoDescription: 'Keramikversiegelung im Saarland: bis zu 5 Jahre Schutz vor UV, Vogelkot und Streusalz, mit Lackkorrektur und Garantie. Mobil oder am Standort. Festpreis.',
    bulletsTitle: 'Das bekommst du bei der Keramikversiegelung',
    h1: 'Keramikversiegelung im Saarland — Lackschutz auf höchstem Niveau',
    headline: 'Keramikversiegelung — Lackschutz auf höchstem Niveau',
    intro: 'Langzeit-Schutz für deinen Lack: Keramikversiegelungen mit bis zu 5 Jahren Standzeit, Glanztiefe wie aus dem Showroom.',
    bullets: [
      'Hochwertige Keramik-Coatings führender Hersteller',
      'Bis zu 5 Jahre Schutz vor UV, Vogelkot, Insekten und Streusalz',
      'Optionale Lackkorrektur vor der Versiegelung',
      'Schriftliches Pflegeprotokoll & Garantieleistung',
    ],
  },
  {
    slug: 'lackpolitur',
    name: 'Lackpolitur',
    plural: 'Lackpolituren',
    seoTitle: 'Lackpolitur Saarland — Hologramme & Kratzer entfernen',
    seoDescription: 'Lackpolitur im Saarland: 1- bis 3-Stufen-Politur mit Schichtdickenmessung. Hologramme und Wirbelspuren weg. Mobil oder am Standort. Festpreis-Angebot.',
    bulletsTitle: 'Das bekommst du bei der Lackpolitur',
    h1: 'Lackpolitur im Saarland — Hologramme weg, Tiefe rein',
    headline: 'Profi-Lackpolitur und Lackkorrektur',
    intro: 'Hologramme weg, Tiefe rein — Lackkorrektur und Politur, die deinen Wagen wieder wie neu aussehen lassen.',
    bullets: [
      '1- bis 3-Stufen-Politur, je nach Lackzustand',
      'Mehrstufige Korrektur für Hologramm-, Wasch- und Wirbelspuren',
      'Schichtdickenmessung vor jeder Politur — kein Material-Risiko',
      'Auf Wunsch direkt mit anschließender Versiegelung kombinierbar',
    ],
  },
];

export const SERVICE_BY_SLUG = Object.fromEntries(SERVICES.map((s) => [s.slug, s]));

// ─── STANDORTSEITEN ─────────────────────────────────────────────────────────
// Feste URLs (/standort/<slug>); Adresse, Zeiten und Telefon kommen aus
// db.locations (Master → Standorte), die Zuordnung läuft über `kind`.
// Der Firmensitz (Am Hirtenhaus 17, Eppelborn) ist Postanschrift und KEIN
// Standort für Kundschaft — er bekommt bewusst keine eigene Seite.
export const LOCATION_PAGES = [
  {
    slug: 'blieskastel',
    kind: 'branch',
    cityMatch: /blieskastel/i,
    city: 'Blieskastel',
    name: 'Niederlassung Saar-Pfalz',
    h1: 'Aufbereiter Blieskastel — CHROBE One World, Niederlassung Saar-Pfalz',
    title: 'Aufbereiter Blieskastel — Niederlassung Saar-Pfalz',
    description: 'CHROBE One World Niederlassung Saar-Pfalz in Blieskastel: Fahrzeugaufbereitung, Keramikversiegelung und Lackpolitur für Blieskastel, Homburg, Zweibrücken und die Saarpfalz. Eröffnung Januar 2027.',
  },
  {
    slug: 'eppelborn',
    kind: 'partner',
    cityMatch: /eppelborn/i,
    city: 'Eppelborn',
    name: "Partnerbetrieb Drea's Polierbude",
    h1: "CHROBE One World in Eppelborn — Partnerbetrieb Drea's Polierbude",
    title: "Aufbereiter Eppelborn — Partnerbetrieb Drea's Polierbude",
    description: "CHROBE One World in Eppelborn: Fahrzeugaufbereitung und Lackversiegelung gemeinsam mit unserem Partnerbetrieb Drea's Polierbude. Termin über CHROBE One World oder direkt beim Partner.",
  },
];

export const LOCATION_PAGE_BY_SLUG = Object.fromEntries(LOCATION_PAGES.map((l) => [l.slug, l]));

// ─── ALTE STADTSEITEN → NEUE ZIELE ──────────────────────────────────────────
// Muss mit `$legacy_city_target` in scripts/vps-configs/chrobe.de.conf
// übereinstimmen. Eppelborn → Partnerseite, Homburg → Blieskastel, sonst die
// Leistungsseite. fahrzeugpflege/detailing sind in /aufbereiter aufgegangen.
export function legacyCityTarget(service, city) {
  if (city === 'eppelborn') return '/standort/eppelborn';
  if (city === 'homburg') return '/standort/blieskastel';
  if (service === 'keramikversiegelung' || service === 'lackpolitur') return `/${service}`;
  return '/aufbereiter';
}

// ─── Alle Pfade der programmatischen Seiten (Frontend-Helper) ───────────────
export function getAllSeoLandingPaths() {
  const paths = ['/waschbox-finder'];
  REGIONS.forEach((r) => paths.push(`/waschbox-finder/${r.slug}`));
  SERVICES.forEach((s) => paths.push(`/${s.slug}`));
  LOCATION_PAGES.forEach((l) => paths.push(`/standort/${l.slug}`));
  return paths;
}
